<template>
  <CTabs variant="tabs" class="nav-tabs-boxed">
    <CTab title="Banners Rotativos">
      <div class="banner-listagem">
        <Loading :isActive="loading" />

        <BannerFormModal
          :item="item"
          :close="closeEdit"
          :filiais="filiais"
          :sistemas="sistemas"
        />

        <div class="banner-listagem__header">
          <h4 class="banner-listagem__title">Banners Rotativos</h4>
          <CButton color="primary" @click="openCreate">
            <CIcon name="cil-plus" size="sm" class="mr-1" />
            Novo Banner
          </CButton>
        </div>

        <div class="banner-listagem__filtros">
          <CRow>
            <CCol md="4">
              <div class="filtro-search-wrapper">
                <CIcon
                  name="cil-magnifying-glass"
                  size="sm"
                  class="filtro-search-icon"
                />
                <input
                  class="form-control filtro-search-input"
                  type="text"
                  v-model="filtros.termo"
                  placeholder="Buscar por nome..."
                  @keyup.enter="loadBanners()"
                />
              </div>
            </CCol>
            <CCol md="3">
              <select
                class="form-control"
                v-model="filtros.status"
                @change="loadBanners()"
              >
                <option value="">Todos os status</option>
                <option value="ATIVO">Ativos</option>
                <option value="AGENDADO">Agendados</option>
                <option value="ENCERRADO">Encerrados</option>
              </select>
            </CCol>
            <CCol md="3">
              <select
                class="form-control"
                v-model="filtros.local"
                @change="loadBanners()"
              >
                <option value="">Todos os locais</option>
                <option v-for="local in locais" :key="local.key" :value="local.key">
                  {{ local.label }}
                </option>
              </select>
            </CCol>
            <CCol md="2">
              <CButton color="primary" block @click="loadBanners()">
                <CIcon name="cil-magnifying-glass" size="sm" class="mr-1" />
                Buscar
              </CButton>
            </CCol>
          </CRow>
        </div>

        <div class="banner-listagem__table-wrapper">
          <table class="banner-listagem__table">
            <thead>
              <tr>
                <th class="col-id">ID</th>
                <th class="col-imagem">Desktop</th>
                <th class="col-imagem">Mobile</th>
                <th class="col-nome">Nome</th>
                <th class="col-status">Status</th>
                <th class="col-data">Início</th>
                <th class="col-data">Fim</th>
                <th class="col-local">Local</th>
                <th class="col-tipo">Tipo</th>
                <th class="col-ordem">Ordem</th>
                <th class="col-acoes"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="banners.length === 0">
                <td colspan="11" class="banner-listagem__empty">
                  <CIcon name="cil-ban" size="xl" class="banner-listagem__empty-icon" />
                  <p>Nenhum banner encontrado</p>
                </td>
              </tr>
              <tr
                v-for="banner in banners"
                :key="banner.id"
                class="banner-listagem__row"
              >
                <td class="col-id">{{ banner.id }}</td>
                <td class="col-imagem">
                  <!--
                    Não existe variante redimensionada para o bucket de arquivos:
                    a miniatura baixa o banner original. `lazy` evita puxar as
                    linhas que estão fora da área visível.
                  -->
                  <img
                    v-if="banner.imagem_desktop"
                    :src="banner.imagem_desktop"
                    class="banner-listagem__thumb"
                    loading="lazy"
                    decoding="async"
                  />
                  <span v-else class="banner-listagem__sem-imagem">—</span>
                </td>
                <td class="col-imagem">
                  <img
                    v-if="banner.imagem_mobile"
                    :src="banner.imagem_mobile"
                    class="banner-listagem__thumb"
                    loading="lazy"
                    decoding="async"
                  />
                  <span v-else class="banner-listagem__sem-imagem">—</span>
                </td>
                <td class="col-nome">
                  <span class="banner-listagem__nome">{{ banner.nome }}</span>
                </td>
                <td class="col-status">
                  <BannerStatusBadge :banner="banner" />
                </td>
                <td class="col-data">{{ formatarData(banner.data_inicio) }}</td>
                <td class="col-data">{{ formatarData(banner.data_fim) }}</td>
                <td class="col-local">
                  <span class="banner-listagem__label">
                    {{ getLocalLabel(banner.local) }}
                  </span>
                </td>
                <td class="col-tipo">
                  <span class="banner-listagem__label">
                    {{ getTipoLabel(banner.tipo) }}
                  </span>
                </td>
                <td class="col-ordem">{{ banner.ordem }}</td>
                <td class="actions">
                  <CButton
                    size="sm"
                    color="secondary"
                    @click="cloneBanner(banner)"
                    title="Duplicar"
                  >
                    <fa-icon icon="clone" />
                  </CButton>
                  <CButton
                    size="sm"
                    color="primary"
                    @click="editBanner(banner)"
                    title="Alterar"
                  >
                    <fa-icon icon="pencil-alt" />
                  </CButton>
                  <CButton
                    size="sm"
                    color="danger"
                    @click="deleteConfirm(banner.id)"
                    title="Excluir"
                  >
                    <fa-icon icon="trash-alt" />
                  </CButton>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="banner-listagem__pagination">
          <pagination
            :data="table.paginator"
            :limit="5"
            @pagination-change-page="loadBanners"
          />
        </div>
      </div>
    </CTab>
  </CTabs>
</template>

<script>
import { get, del } from "@/helpers/apiRequest";
import moment from "moment";
import clone from "just-clone";
import Loading from "@/components/Loading.vue";
import BannerFormModal from "./BannerFormPage.vue";
import BannerStatusBadge from "../shared/BannerStatusBadge.vue";
import { defaultBanner, cloneBannerParaCriacao } from "../helpers/bannerDefaults";
import { LOCAIS, getLocalLabel, getTipoLabel } from "../helpers/bannerLabels";

export default {
  name: "BannerListagem",
  components: {
    Loading,
    BannerFormModal,
    BannerStatusBadge,
  },
  props: ["sistemas"],
  data() {
    return {
      loading: false,
      item: defaultBanner(),
      banners: [],
      filiais: [],
      locais: LOCAIS,
      filtros: {
        termo: "",
        status: "",
        local: "",
      },
      table: {
        paginator: {},
      },
    };
  },
  methods: {
    getLocalLabel,
    getTipoLabel,
    formatarData(valor) {
      return valor ? moment(String(valor)).format("DD/MM/YYYY") : "—";
    },
    loadBanners(page = 1) {
      this.loading = true;

      const params = new URLSearchParams({
        page,
        termo: this.filtros.termo,
        status: this.filtros.status,
        local: this.filtros.local,
      });

      get(`/admin/marketing/banners?${params.toString()}`)
        .then((json) => {
          this.banners = json.itens;
          this.table.paginator = json.paginator;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openCreate() {
      this.item = defaultBanner();
      this.$modal.show("bannerEdit");
    },
    editBanner(banner) {
      this.item = clone(banner);
      this.$modal.show("bannerEdit");
    },
    cloneBanner(banner) {
      this.item = cloneBannerParaCriacao(clone(banner));
      this.$modal.show("bannerEdit");
    },
    closeEdit() {
      this.$modal.hide("bannerEdit");
      this.loadBanners(this.table.paginator.current_page || 1);
    },
    deleteConfirm(id) {
      this.$swal({
        title: "Confirma Remover?",
        text: "Essa ação não poderá ser desfeita",
        showCancelButton: true,
        icon: "warning",
        confirmButtonText: "Excluir",
        cancelButtonText: "Cancelar",
      })
        .then((res) => {
          if (res.value) {
            this.loading = true;
            del(`/admin/marketing/banners/${id}`)
              .then(() => {
                this.loadBanners(this.table.paginator.current_page || 1);
              })
              .catch(() => {
                this.$swal({
                  title: "Falha ao remover",
                  icon: "error",
                });
              })
              .finally(() => {
                this.loading = false;
              });
          }
        })
        .catch(() => {});
    },
  },
  beforeMount() {
    this.loadBanners();

    const { filiais } = JSON.parse(localStorage.getItem("app"));
    this.filiais = filiais;
  },
};
</script>

<style scoped>
/* ===== HEADER ===== */
.banner-listagem__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.banner-listagem__title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #2c2c2c;
}

/* ===== FILTROS ===== */
.banner-listagem__filtros {
  margin-bottom: 20px;
  padding: 16px;
  background: #f9fafb;
  border: 1px solid #e4e7eb;
  border-radius: 10px;
}

.banner-listagem__filtros .form-control {
  font-size: 13px;
  border-radius: 6px;
  border: 1px solid #dddce9;
}

.banner-listagem__filtros .form-control:focus {
  border-color: #3a3e81;
  box-shadow: 0 0 0 2px rgba(58, 62, 129, 0.1);
}

.filtro-search-wrapper {
  position: relative;
}

.filtro-search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #98a2b3;
  z-index: 1;
}

.filtro-search-input {
  padding-left: 36px !important;
}

/* ===== TABLE ===== */
.banner-listagem__table-wrapper {
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  overflow-x: auto;
  background: #fff;
  margin-bottom: 20px;
}

.banner-listagem__table {
  width: 100%;
  border-collapse: collapse;
}

.banner-listagem__table thead tr {
  background: #f9fafb;
  border-bottom: 1px solid #e4e7eb;
}

.banner-listagem__table th {
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  padding: 12px 12px;
  white-space: nowrap;
}

.banner-listagem__table td {
  padding: 12px 12px;
  border-bottom: 1px solid #f2f4f7;
  font-size: 13px;
  color: #344054;
  vertical-align: middle;
}

.banner-listagem__row:last-child td {
  border-bottom: none;
}

.banner-listagem__row:hover {
  background: #f9fafb;
}

.col-id { width: 60px; text-align: center; }
.col-imagem { width: 110px; text-align: center; }
.col-status { width: 100px; text-align: center; }
.col-data { width: 100px; text-align: center; }
.col-local { width: 130px; text-align: center; }
.col-tipo { width: 120px; text-align: center; }
.col-ordem { width: 70px; text-align: center; }
.col-acoes { width: 120px; text-align: center; }

.banner-listagem__thumb {
  max-width: 90px;
  max-height: 40px;
  border-radius: 4px;
  border: 1px solid #e4e7eb;
  object-fit: cover;
}

.banner-listagem__sem-imagem {
  color: #d0d5dd;
}

.banner-listagem__nome {
  font-weight: 500;
  color: #1d2939;
}

.banner-listagem__label {
  font-size: 12px;
  color: #555;
}

/* ===== EMPTY STATE ===== */
.banner-listagem__empty {
  text-align: center;
  padding: 48px 20px !important;
  color: #98a2b3;
}

.banner-listagem__empty-icon {
  color: #d0d5dd;
  margin-bottom: 8px;
}

.banner-listagem__empty p {
  font-size: 13px;
  margin: 0;
}

/* ===== PAGINATION ===== */
.banner-listagem__pagination {
  display: flex;
  justify-content: center;
}
</style>
